import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import { SERVICES, submitLead, formatApiError, whatsappLeadUrl } from "@/lib/api";

const FIELD_DEFS = {
  name: { key: "name", label: "Full Name", type: "text", placeholder: "Your full name", required: true },
  phone: { key: "phone", label: "Phone", type: "tel", placeholder: "10-digit mobile number", required: true },
  email: { key: "email", label: "Email", type: "email", placeholder: "you@example.com", required: false },
  service: { key: "service", label: "Service Required", type: "select", required: false },
  message: { key: "message", label: "Message", type: "textarea", placeholder: "Tell us a little more (optional)", required: false },
};

export default function LeadForm({
  source,
  fields = ["name", "phone", "service"],
  services = SERVICES,
  cta = "Request Callback",
  idPrefix = "lead",
}) {
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = (values.name || "").trim();
    const phone = (values.phone || "").trim();
    if (!name || !phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    setSending(true);
    try {
      const lead = {
        name,
        phone,
        email: (values.email || "").trim() || null,
        service: (values.service || services[0]).trim(),
        message: (values.message || "").trim() || null,
        source,
      };
      await submitLead(lead);
      const url = whatsappLeadUrl(lead);
      setWaUrl(url);
      setDone(true);
      setValues({});
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Thank you, ${name.split(" ")[0]}! Our team will reach out shortly.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid={`${idPrefix}-success`}>
        <CheckCircle2 className="h-12 w-12 text-emerald-500" />
        <p className="font-display text-xl font-bold text-navy">Request received.</p>
        <p className="max-w-xs text-sm text-slate-600">
          A member of our team will call you back. Tap below to send us your request on WhatsApp for the fastest response.
        </p>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-2 rounded-full bg-whatsapp px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-110"
          data-testid={`${idPrefix}-whatsapp-alert-btn`}
        >
          <MessageCircle className="h-4 w-4" /> Send on WhatsApp
        </a>
        <button type="button" className="mt-2 text-sm font-bold text-amber-600 underline underline-offset-4" onClick={() => setDone(false)}>
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
      {fields.map((fieldName) => {
        const def = FIELD_DEFS[fieldName];
        if (!def) return null;
        if (def.type === "select") {
          return (
            <div key={fieldName} className={fieldName === "service" ? "sm:col-span-2" : ""}>
              <label className="ee-label" htmlFor={`${idPrefix}-${fieldName}`}>{def.label}</label>
              <select
                id={`${idPrefix}-${fieldName}`}
                className="ee-input appearance-none"
                value={values.service || ""}
                onChange={set("service")}
                data-testid={`${idPrefix}-service-select`}
              >
                {services.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          );
        }
        if (def.type === "textarea") {
          return (
            <div key={fieldName} className="sm:col-span-2">
              <label className="ee-label" htmlFor={`${idPrefix}-${fieldName}`}>{def.label}</label>
              <textarea
                id={`${idPrefix}-${fieldName}`}
                rows={3}
                className="ee-input resize-none"
                placeholder={def.placeholder}
                value={values.message || ""}
                onChange={set("message")}
                data-testid={`${idPrefix}-message-input`}
              />
            </div>
          );
        }
        return (
          <div key={fieldName}>
            <label className="ee-label" htmlFor={`${idPrefix}-${fieldName}`}>{def.label}</label>
            <input
              id={`${idPrefix}-${fieldName}`}
              type={def.type}
              placeholder={def.placeholder}
              className="ee-input"
              value={values[fieldName] || ""}
              onChange={set(fieldName)}
              data-testid={`${idPrefix}-${fieldName}-input`}
            />
          </div>
        );
      })}
      <button
        type="submit"
        disabled={sending}
        className="btn-gold sm:col-span-2 disabled:cursor-not-allowed disabled:opacity-60"
        data-testid={`${idPrefix}-submit-btn`}
      >
        {sending ? "Sending…" : cta}
        {!sending && <ArrowRight className="h-4 w-4" />}
      </button>
    </form>
  );
}
